/**
 * What the dashboard shows, worked out from the saved rows and the card types.
 * No React and no network: the page loads, this decides.
 *
 * ## The rules
 *
 * 1. **The saved rows come first, in their order.**
 * 2. **Then every default card that has no row**, by the type's `order`, then
 *    by catalogue order (core, then `MODULES` order), then in the order its
 *    type lists them. With no rows at all, that is the whole default dashboard.
 *    A new module, or a permission given, adds its defaults at the end.
 * 3. **A removed default keeps its row, hidden**, so rule 2 does not bring it
 *    back. A removed card the user added is simply gone.
 * 4. **A row whose type is unknown or not for this caller is kept but not
 *    shown.** A module removed, or a permission lost, does not erase the
 *    person's layout; it comes back in its place with the permission.
 * 5. **The width is clamped** to the type's limits, and **the config is checked**
 *    against the type's `defaultConfig`, every time it is shown. Both were
 *    stored by an older version of the code, maybe.
 */
import { DASHBOARD_LIMITS, type DashboardCardDto, type DashboardCardWidth } from '@forsys/shared';
import type {
  DashboardCardType,
  DashboardConfig,
  DashboardContext,
  DashboardDefault,
  FrontendModule,
} from '../../modules/types';

/** A card type under its stored name — `core:stat`, `coffee:machine`. */
export interface CatalogueEntry {
  readonly key: string;
  readonly type: DashboardCardType;
  /** Its place in the catalogue, for a tie in `order`. */
  readonly index: number;
}

/** A card on the dashboard. `id` is this browser's own, for React and for dragging; it is not stored. */
export interface DashboardItem extends DashboardCardDto {
  readonly id: string;
}

/** A card that renders: its type is known and for this caller, and it is not hidden. */
export interface ShownCard {
  readonly item: DashboardItem;
  readonly entry: CatalogueEntry;
  readonly width: DashboardCardWidth;
  readonly config: DashboardConfig;
}

/** The default cards of one type, as its `defaults` answered. */
export interface TypeDefaults {
  readonly entry: CatalogueEntry;
  readonly defaults: readonly DashboardDefault<DashboardConfig>[];
}

/** Core's card types, then each module's. Throws on a name used twice: a mistake in a manifest. */
export function cardCatalogue(
  core: readonly DashboardCardType[],
  modules: readonly FrontendModule[],
): CatalogueEntry[] {
  const named = [
    ...core.map((type) => ({ key: `core:${type.id}`, type })),
    ...modules.flatMap((module) =>
      (module.dashboardCards ?? []).map((type) => ({ key: `${module.key}:${type.id}`, type })),
    ),
  ];
  const seen = new Set<string>();
  for (const { key } of named) {
    if (seen.has(key)) throw new Error(`Dashboard card type "${key}" is declared twice.`);
    seen.add(key);
  }
  return named.map((entry, index) => ({ ...entry, index }));
}

/** The types this caller may see, lightest first: the order defaults appear in and "Add card" lists. */
export function availableTypes(catalogue: readonly CatalogueEntry[], ctx: DashboardContext): CatalogueEntry[] {
  return catalogue
    .filter((entry) => !entry.type.anyOf || ctx.canAny(...entry.type.anyOf))
    .sort((a, b) => a.type.order - b.type.order || a.index - b.index);
}

/**
 * Asks each type for its defaults. A type whose `defaults` fails has none this
 * time: nothing is stored for it, so it is asked again on the next load.
 */
export async function resolveDefaults(
  types: readonly CatalogueEntry[],
  ctx: DashboardContext,
): Promise<TypeDefaults[]> {
  return Promise.all(
    types.map(async (entry) => {
      try {
        return { entry, defaults: entry.type.defaults ? await entry.type.defaults(ctx) : [] };
      } catch {
        return { entry, defaults: [] };
      }
    }),
  );
}

const defaultId = (type: string, key: string): string => `${type} ${key}`;

/** Rules 1–3: the saved rows, then the defaults with no row. At most `DASHBOARD_LIMITS.CARDS`. */
export function arrangeLayout(saved: readonly DashboardCardDto[], defaults: readonly TypeDefaults[]): DashboardItem[] {
  const items: DashboardItem[] = saved.map((card, index) => ({ ...card, id: `card-${index}` }));
  const stored = new Set(
    saved.flatMap((card) => (card.defaultKey === null ? [] : [defaultId(card.type, card.defaultKey)])),
  );

  for (const { entry, defaults: list } of defaults) {
    for (const found of list) {
      if (items.length >= DASHBOARD_LIMITS.CARDS) return items;
      const id = defaultId(entry.key, found.key);
      if (stored.has(id)) continue;
      stored.add(id);
      items.push({
        id: `default-${id}`,
        type: entry.key,
        config: { ...found.config },
        defaultKey: found.key,
        width: entry.type.width,
        hidden: false,
      });
    }
  }
  return items;
}

export function clampWidth(width: number, type: DashboardCardType): DashboardCardWidth {
  const min = type.minWidth ?? 1;
  const max = type.maxWidth ?? DASHBOARD_LIMITS.COLUMNS;
  return Math.min(Math.max(width, min), max) as DashboardCardWidth;
}

/**
 * A stored config, made safe: every key of `defaultConfig`, with the stored
 * value where it has the same type, and the default where it is missing or
 * wrong. Keys the type does not know are dropped.
 */
export function normaliseConfig(defaults: DashboardConfig, stored: Readonly<Record<string, unknown>>): DashboardConfig {
  const result: Record<string, string | number | boolean> = {};
  for (const [key, fallback] of Object.entries(defaults)) {
    const value = stored[key];
    result[key] = typeof value === typeof fallback ? (value as string | number | boolean) : fallback;
  }
  return result;
}

/** Rules 4 and 5: what renders, in order. */
export function shownCards(items: readonly DashboardItem[], available: readonly CatalogueEntry[]): ShownCard[] {
  const byKey = new Map(available.map((entry) => [entry.key, entry]));
  return items.flatMap((item) => {
    const entry = byKey.get(item.type);
    if (!entry || item.hidden) return [];
    return [
      {
        item,
        entry,
        width: clampWidth(item.width, entry.type),
        config: normaliseConfig(entry.type.defaultConfig, item.config),
      },
    ];
  });
}

// --- editing -----------------------------------------------------------------

/** Puts `fromId` where `toId` is. The cards between move by one. */
export function moveItem(items: readonly DashboardItem[], fromId: string, toId: string): DashboardItem[] {
  const from = items.findIndex((item) => item.id === fromId);
  const to = items.findIndex((item) => item.id === toId);
  if (from === -1 || to === -1 || from === to) return [...items];
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}

/** Rule 3: a default is hidden, so that it stays away; a card the user added goes. */
export function removeItem(items: readonly DashboardItem[], id: string): DashboardItem[] {
  return items.flatMap((item) => {
    if (item.id !== id) return [item];
    return item.defaultKey === null ? [] : [{ ...item, hidden: true }];
  });
}

export function updateItem(
  items: readonly DashboardItem[],
  id: string,
  change: Partial<Pick<DashboardCardDto, 'width' | 'config'>>,
): DashboardItem[] {
  return items.map((item) => (item.id === id ? { ...item, ...change } : item));
}

/** A new card of this type at the end, with the type's default config and width. */
export function addItem(items: readonly DashboardItem[], entry: CatalogueEntry, id: string): DashboardItem[] {
  return [
    ...items,
    {
      id,
      type: entry.key,
      config: { ...entry.type.defaultConfig },
      defaultKey: null,
      width: entry.type.width,
      hidden: false,
    },
  ];
}

/** What is saved: every row, hidden and unknown ones too, without this browser's ids. */
export function toSaved(items: readonly DashboardItem[]): DashboardCardDto[] {
  return items.map(({ id: _id, ...card }) => card);
}
